import { useLocation, Navigate } from "react-router-dom";
import { loginRoute } from "../routes/reactRoutes";
import { canAccessPathname } from "../utils/routeAccess";
import UnAuthorized from "../components/commonControls/UnAuthorized";
import verifyToken from "./CheckToken";
import Layout from "../components/layout/Layout";
import { Box } from "@mui/material";
import drawerDim from "../theme/drawerDimensions";
import { useSelector, useDispatch } from "react-redux";
import {
  meRoute,
  changeLocationRoute,
  consentStatusRoute,
} from "../routes/apiRoutes";
import { toast } from "react-toastify";
import { login, updateConsent } from "../Redux/authenticationSlice";
import { useEffect, useState } from "react";
import Loading from "../components/commonControls/loading";
import useTheme from "@mui/material/styles/useTheme";
import { apply } from "../Redux/themeSlice";
import { EllipsisTooltips } from "../components/commonControls/EllipsisTooltips";
import Tooltip from "../components/commonControls/TooltipCustom";
import axiosInstance from "../axios/axiosInstance";
import { convertUrlToBase64 } from "../utils/avatarUtils";
import ConsentRequiredModal from "../components/authentication/ConsentRequiredModal";
import "./langAnimation.css";

export const PrivateRoute = ({ children }) => {
  const sidebarCollapsed = useSelector((state) => state.collapseValues.sidebarCollapsed);
  const theme = useTheme();
  const drawerMargins = {
    marginLeft: { xs:0, sm: `${drawerDim.collapsedWidth}px`, md: `${sidebarCollapsed ? drawerDim.collapsedWidth : drawerDim.width}px` },
    background: theme.palette.componentBackgrounds.page,
    marginTop: `${drawerDim.height}px`,
    marginBottom: `${drawerDim.bottom}px`,
    minHeight: `calc(100vh - ${drawerDim.height}px - ${drawerDim.bottom}px)`,
  };
  const location = useLocation();
  const isChangingDirection = useSelector(
    (state) => state.themeValues.isChangingDirection
  );
  const dispatch = useDispatch();
  const currentUser = useSelector(
    (state) => state.authenticationValues.currentUser
  ); 
  const colors = useSelector((state) => state.themeValues.colors);
  const [userVerified, setUserVerified] = useState(false);
  const [tooltipElement, setTooltipElement] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [requiresConsent, setRequiresConsent] = useState(false);

  const handleShowTooltip = (element) => {
    setTooltipElement(element);
  };
  const [isLoading, setIsLoading] = useState(true);

  localStorage.setItem(
    "prevLocation",
    `${location.pathname}${location.search}`
  );

  useEffect(() => {
    if (children?.props?.children?.props?.title) {
      document.title = children?.props.children.props.title;
    }
  }, [children]);

  useEffect(() => {
    const handleToken = async () => {
      try {

        const response = await axiosInstance.get(meRoute, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const {data} = response;
        if (response.status === 200 || response.status === 201) {
          let safeUser = data.data.user;
          const currentId = safeUser?.currentLocation?.id;
          const allowedIds = Array.isArray(safeUser?.locationIds) ? safeUser.locationIds : [];
          if (currentId && allowedIds.length > 0 && !allowedIds.includes(currentId)) {
            const fallbackId = allowedIds[0];
            await axiosInstance.post(
              changeLocationRoute,
              { locationId: fallbackId },
              { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );
            const meRes = await axiosInstance.get(meRoute, {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            safeUser = meRes?.data?.data?.user || safeUser;
          }

          // Convert avatarUrl to base64 if present (use cached value if possible)
          const nextAvatarUrl = safeUser?.avatarUrl || "";
          const cachedAvatarUrl = localStorage.getItem("avatarUrl") || "";
          let avatarBase64 = localStorage.getItem("avatar") || null;
          if (nextAvatarUrl && (!avatarBase64 || cachedAvatarUrl !== nextAvatarUrl)) {
            avatarBase64 = await convertUrlToBase64(nextAvatarUrl);
            localStorage.setItem("avatarUrl", nextAvatarUrl);
            if (avatarBase64) localStorage.setItem("avatar", avatarBase64);
          }

          dispatch(
            login({
              user: safeUser,
              token: null,
              avatar: avatarBase64,
            })
          );
          dispatch(
            apply({
              colors: colors,
              mainColor: safeUser?.currentLocation?.colorPalette || "#BB2436",
              mode: "light",
            })
          );
          setUserVerified(true);
        } else {
          toast.dismiss();
          toast.error(`Unexpected response`, { autoClose: false });
        }
      } catch (error) {}
      setIsLoading(false);
    };
    if (!currentUser.firstName) handleToken();
    else setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resolveConsent = async () => {
      if (!currentUser?.firstName && !userVerified) {
        return;
      }

      const existing = currentUser?.consent;
      if (existing && typeof existing.requiresConsent === "boolean") {
        setRequiresConsent(!!existing.requiresConsent);
        setConsentChecked(true);
        return;
      }

      try {
        const { data } = await axiosInstance.get(consentStatusRoute, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (data.status === "success" || data.statusCode === 200) {
          dispatch(updateConsent(data.data));
          setRequiresConsent(!!data.data?.requiresConsent);
        } else {
          setRequiresConsent(true);
        }
      } catch {
        setRequiresConsent(true);
      } finally {
        setConsentChecked(true);
      }
    };

    if ((currentUser?.firstName || userVerified) && !consentChecked) {
      resolveConsent();
    }
  }, [currentUser, userVerified, consentChecked, dispatch]);

  if ((!currentUser.firstName && verifyToken() && isLoading) || ((currentUser.firstName || userVerified) && !consentChecked)) {
    return <Loading />;
  }

  const isAuthed = currentUser.firstName || userVerified;
  const pathAllowed =
    !isAuthed ||
    canAccessPathname(
      location.pathname,
      currentUser.permissions,
      currentUser.roles
    );

  const consentBlocking =
    isAuthed &&
    (requiresConsent || currentUser?.consent?.requiresConsent === true);

  return isAuthed ? (
      <>
        <Layout />
        <EllipsisTooltips onShowTooltip={handleShowTooltip} />
        <Tooltip element={tooltipElement} />

        <ConsentRequiredModal
          open={consentBlocking}
          onAccepted={(consent) => {
            setRequiresConsent(false);
            dispatch(updateConsent(consent));
          }}
        />

        <Box
          sx={{
            ...drawerMargins,
            animation: isChangingDirection
              ? "vanishOut 0.5s ease-in-out forwards"
              : "vanishIn 0.5s ease-in-out forwards",
            willChange: "filter",
            // Dim / block interaction until consent is given
            pointerEvents: consentBlocking ? "none" : "auto",
            filter: consentBlocking ? "blur(1px)" : "none",
            opacity: consentBlocking ? 0.45 : 1,
          }}
        >
          {pathAllowed ? (
            children
          ) : (
            <UnAuthorized />
          )}
        </Box>
      </>
  ) : (
    <Navigate to={loginRoute} state={{ from: location }} replace />
  );
};
